import { useCallback, useEffect, useRef } from 'react';
import type { VehicleScheduleContext } from '@/app/utils/vehicleScheduleContext';
import type { ConfirmSmriqVehiclePayload } from '../services/smriq';
import { smriqSessionVehicleKey } from '../utils/smriqSessionVehicleKey';

interface UseSmriqAutoConfirmArgs {
    productId: string | undefined;
    selectedVehicle: Parameters<typeof smriqSessionVehicleKey>[0];
    defaultVrm: string;
    vehicleContext: VehicleScheduleContext;
    servicePlanSessionId: string | null | undefined;
    isConfirming: boolean;
    confirmError: unknown;
    confirmVehicle: (payload: ConfirmSmriqVehiclePayload) => void;
}

export function useSmriqAutoConfirm({
    productId,
    selectedVehicle,
    defaultVrm,
    vehicleContext,
    servicePlanSessionId,
    isConfirming,
    confirmError,
    confirmVehicle,
}: UseSmriqAutoConfirmArgs) {
    // A local plan needs no confirm step; SMRiQ mirrors that by confirming from the
    // quote vehicle rather than asking the dealer to retype what they just entered.
    const canAutoConfirm =
        !!productId &&
        defaultVrm !== '' &&
        vehicleContext.vehicleMileage !== undefined &&
        vehicleContext.annualMileage !== undefined;

    // What the vendor would be asked to price. Selecting a replacement vehicle clears the
    // session, so a fresh tuple must re-arm the billed confirm rather than stay disarmed.
    const autoConfirmKey = `${productId ?? ''}|${smriqSessionVehicleKey(selectedVehicle)}`;
    const autoConfirmedKeyRef = useRef<string | null>(null);

    useEffect(() => {
        if (autoConfirmedKeyRef.current === autoConfirmKey) return;
        if (servicePlanSessionId || isConfirming || confirmError || !canAutoConfirm) return;
        autoConfirmedKeyRef.current = autoConfirmKey;
        confirmVehicle({
            vrm: defaultVrm,
            current_mileage: vehicleContext.vehicleMileage as number,
            annual_mileage: vehicleContext.annualMileage as number,
        });
    }, [
        autoConfirmKey,
        servicePlanSessionId,
        isConfirming,
        confirmError,
        canAutoConfirm,
        defaultVrm,
        vehicleContext.vehicleMileage,
        vehicleContext.annualMileage,
        confirmVehicle,
    ]);

    const resetAutoConfirm = useCallback(() => {
        autoConfirmedKeyRef.current = null;
    }, []);

    return { canAutoConfirm, resetAutoConfirm };
}
